/**
 * Anchor Scroll (E20-1160)
 *
 * Generic in-page smooth scrolling: any on-page anchor link (`<a href="#id">`) —
 * a footnote marker, a "scroll to…" link, anywhere — smoothly scrolls to its
 * target, offset below the sticky header. Reduced-motion aware, moves focus to
 * the target for accessibility, and updates the URL hash. Also re-applies the
 * offset when the page is opened at a #hash.
 *
 * Skips links that aren't in-page scrolls: drawer/modal triggers
 * (`js-drawer-open-*` / `js-modal-open-*`), the skip-to-content link, bare `#`,
 * and any link marked `data-no-smooth`.
 */
class AnchorScroll {
  constructor() {
    document.addEventListener('click', (e) => this.onClick(e));
    window.addEventListener('load', () => this.onLoad(), { once: true });
  }

  reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Sticky-header height the theme maintains, else measured from the header,
  // plus the sticky announcement bar when one is shown.
  headerOffset() {
    let h = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    if (!h || Number.isNaN(h)) {
      const header = document.querySelector('.site-header');
      h = header ? header.getBoundingClientRect().height : 0;
    }
    const announce =
      parseFloat(
        getComputedStyle(document.body).getPropertyValue('--sticky-announcement-bar-height')
      ) || 0;
    return h + announce + 16; // small breathing gap
  }

  eligible(link) {
    const href = link.getAttribute('href');
    if (!href || href === '#' || href.charAt(0) !== '#') return false;
    if (link.hasAttribute('data-no-smooth')) return false;
    // Drawer/modal triggers and the skip link manage their own behavior.
    if (link.matches('[class*="js-drawer-open"], [class*="js-modal-open"], .skip-link'))
      return false;
    return true;
  }

  onClick(e) {
    if (e.defaultPrevented) return;
    // Let the browser handle modified / non-primary clicks (open in new tab, etc.).
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!(e.target instanceof Element)) return;
    const link = e.target.closest('a[href^="#"]');
    if (!link || !this.eligible(link)) return;
    const href = link.getAttribute('href');
    const target = this.resolve(href);
    if (!target) {
      // Unknown target — hand back to the browser, which is the only correct behaviour here.
      // But say so: a dead in-page anchor produces no error and no movement, so it is invisible
      // in QA and reads to a shopper as a broken link. Seven PDPs shipped a footnote marker
      // pointing at a #footnotes block that never rendered, and nothing surfaced it (E20-1427).
      console.warn(`[bfx-anchor-scroll] no element matches ${href} — the link will do nothing.`);
      return;
    }
    e.preventDefault();
    this.scrollToTarget(target, true);
  }

  onLoad() {
    const target = this.resolve(window.location.hash);
    if (target) requestAnimationFrame(() => this.scrollToTarget(target, false));
  }

  resolve(hash) {
    if (!hash || hash === '#') return null;
    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      return null; // malformed percent-encoding — treat as non-resolvable
    }
    return document.getElementById(id);
  }

  // `behavior` lets a caller opt out of the animation while keeping the header
  // offset and focus handling — see bfx-bazaarvoice-scroll (E20-1391), where the
  // Bazaarvoice widget starves the main thread and stalls a smooth scroll.
  scrollToTarget(target, updateHash, behavior = 'smooth') {
    const top = target.getBoundingClientRect().top + window.scrollY - this.headerOffset();
    window.scrollTo({ top, behavior: this.reduceMotion() ? 'auto' : behavior });

    // Move focus to the target without re-scrolling, so keyboard / screen-reader
    // users land there too.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });

    if (updateHash && target.id) {
      history.pushState(null, '', `#${target.id}`);
    }
  }
}

if (!window.bfxAnchorScroll) {
  window.bfxAnchorScroll = new AnchorScroll();
}
